import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Calendar, Loader2 } from 'lucide-react';
import { upcomingDates } from '../mock';
import './BookingForm.css';

const BookingForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredDate: '',
    dietaryNeeds: '',
    allergies: '',
    yogaExperience: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // Mock submission - will integrate with backend Stripe
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Store form data in localStorage (mock)
      const bookings = JSON.parse(localStorage.getItem('bookings') || '[]');
      bookings.push({
        ...formData,
        id: Date.now(),
        timestamp: new Date().toISOString(),
        status: 'pending'
      });
      localStorage.setItem('bookings', JSON.stringify(bookings));
      
      setSubmitMessage('✓ Booking request received! Redirecting to payment...');
      
      // In production, this will redirect to Stripe checkout
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          preferredDate: '',
          dietaryNeeds: '',
          allergies: '',
          yogaExperience: ''
        });
        setSubmitMessage('Mock: Payment integration coming soon!');
      }, 2000);
      
    } catch (error) {
      setSubmitMessage('Error submitting booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="booking-form-card">
      <CardHeader>
        <CardTitle className="form-title">Begin Your Transformation</CardTitle>
        <CardDescription className="form-description">
          Complete this form to reserve your spot. You'll be redirected to secure payment after submission.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="booking-form">
          <div className="form-group">
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              type="text"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
              className="form-input"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
                className="form-input"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                required
                className="form-input"
              />
            </div>
          </div>

          <div className="form-group">
            <Label htmlFor="preferredDate">Preferred Retreat Date *</Label>
            <Select onValueChange={(value) => handleChange('preferredDate', value)} required>
              <SelectTrigger className="form-select">
                <Calendar className="select-icon" />
                <SelectValue placeholder="Select your preferred dates" />
              </SelectTrigger>
              <SelectContent>
                {upcomingDates.map(date => (
                  <SelectItem key={date.id} value={`${date.startDate} - ${date.endDate}`}>
                    {date.startDate} - {date.endDate} ({date.spotsLeft} spots left)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="form-group">
            <Label htmlFor="yogaExperience">Yoga Experience Level *</Label>
            <Select onValueChange={(value) => handleChange('yogaExperience', value)} required>
              <SelectTrigger className="form-select">
                <SelectValue placeholder="Select your experience level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="beginner">Beginner - New to yoga</SelectItem>
                <SelectItem value="intermediate">Intermediate - Regular practice</SelectItem>
                <SelectItem value="advanced">Advanced - Experienced practitioner</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="form-group">
            <Label htmlFor="dietaryNeeds">Dietary Needs</Label>
            <Textarea
              id="dietaryNeeds"
              placeholder="Vegetarian, vegan, gluten-free, etc."
              value={formData.dietaryNeeds}
              onChange={(e) => handleChange('dietaryNeeds', e.target.value)}
              className="form-textarea"
              rows={3}
            />
          </div>

          <div className="form-group">
            <Label htmlFor="allergies">Allergies or Health Concerns</Label>
            <Textarea
              id="allergies"
              placeholder="Please list any allergies or health concerns we should know about"
              value={formData.allergies}
              onChange={(e) => handleChange('allergies', e.target.value)}
              className="form-textarea"
              rows={3}
            />
          </div>

          {submitMessage && (
            <div className={`submit-message ${submitMessage.includes('Error') ? 'error' : 'success'}`}>
              {submitMessage}
            </div>
          )}

          <Button
            type="submit"
            size="lg"
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="animate-spin" />
                Processing...
              </>
            ) : (
              'Reserve & Proceed to Payment'
            )}
          </Button>

          <p className="form-footer-text">
            By submitting this form, you agree to our terms and conditions. Your payment will be processed securely through Stripe.
          </p>
        </form>
      </CardContent>
    </Card>
  );
};

export default BookingForm;